import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaEnvelope, FaPhone, FaMapMarkerAlt, FaFacebook, FaLinkedin, FaTwitter, FaYoutube } from 'react-icons/fa';
import axios from 'axios';

const API_URL = 'https://aamirsaba.com/api';

const Contact = () => {
  const [contactInfo, setContactInfo] = useState({
    whatsapp: '+1 (XXX) XXX-XXXX',
    email: 'info@aamirsaba.com',
    phone: '+1 (XXX) XXX-XXXX',
    ukAddress: 'London, United Kingdom',
    usaAddress: 'New York, USA',
    facebook: 'https://facebook.com/aamirsaba',
    linkedin: 'https://linkedin.com/company/aamirsaba',
    twitter: 'https://twitter.com/aamirsaba',
    youtube: 'https://youtube.com/aamirsaba',
    responseTime: 'We typically respond within 24 hours',
    formTitle: 'Get In Touch',
    formSubtitle: "Let's discuss how we can help your business grow",
    successMessage: '✅ Message sent successfully!'
  });

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load contact data from API
  useEffect(() => {
    axios.get(`${API_URL}/contact`)
      .then(response => {
        console.log('Contact data loaded:', response.data);
        setContactInfo(prev => ({ ...prev, ...response.data }));
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading contact data:', error);
        setLoading(false);
      });
  }, []);

  const handleChange = (e) => { 
    setFormData({ ...formData, [e.target.name]: e.target.value }); 
  };
  
  const handleSubmit = (e) => { 
    e.preventDefault(); 
    setSubmitted(true);
    
    // Send to the submit endpoint
    axios.post(`${API_URL}/contact/submit`, formData)
      .then(response => {
        console.log('Lead saved:', response.data);
      })
      .catch(err => {
        console.error('Error:', err);
        setSubmitted(false);
      });
  };

  if (loading) {
    return (
      <section className="pt-32 pb-20 bg-navy min-h-screen">
        <div className="container mx-auto px-6 text-center">
          <div className="text-cyan text-xl">Loading...</div>
        </div>
      </section>
    );
  }

  const cleanWhatsApp = contactInfo.whatsapp ? contactInfo.whatsapp.replace(/[^0-9]/g, '') : '';

  return (
    <section className="pt-32 pb-20 bg-navy min-h-screen">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold text-center">
            {contactInfo.formTitle || 'Get In Touch'}
          </h1>
          <p className="text-gray-400 text-center mt-4">{contactInfo.formSubtitle || "Let's discuss how we can help your business grow"}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
            {/* Left Column - Contact Info */}
            <div>
              <div className="space-y-6">
                <a href={`https://wa.me/${cleanWhatsApp}`} 
                   target="_blank" rel="noopener noreferrer" 
                   className="flex items-center gap-4 p-4 bg-darkNavy rounded-xl border border-gray-800 hover:border-green-500 transition cursor-pointer group">
                  <FaWhatsapp className="text-3xl text-green-500" />
                  <div>
                    <p className="text-sm text-gray-400">WhatsApp</p>
                    <p className="text-white font-semibold group-hover:text-green-500 transition">{contactInfo.whatsapp || '+1 (XXX) XXX-XXXX'}</p>
                  </div>
                </a>

                <a href={`mailto:${contactInfo.email}`} 
                   className="flex items-center gap-4 p-4 bg-darkNavy rounded-xl border border-gray-800 hover:border-cyan transition cursor-pointer group">
                  <FaEnvelope className="text-3xl text-cyan" />
                  <div>
                    <p className="text-sm text-gray-400">Email</p>
                    <p className="text-white font-semibold group-hover:text-cyan transition">{contactInfo.email || 'info@aamirsaba.com'}</p>
                  </div>
                </a>

                <a href={`tel:${contactInfo.phone}`} 
                   className="flex items-center gap-4 p-4 bg-darkNavy rounded-xl border border-gray-800 hover:border-cyan transition cursor-pointer group">
                  <FaPhone className="text-3xl text-cyan" />
                  <div>
                    <p className="text-sm text-gray-400">Phone</p>
                    <p className="text-white font-semibold group-hover:text-cyan transition">{contactInfo.phone || '+1 (XXX) XXX-XXXX'}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-darkNavy rounded-xl border border-gray-800">
                  <FaMapMarkerAlt className="text-3xl text-cyan" />
                  <div>
                    <p className="text-sm text-gray-400">🇬🇧 UK Office</p>
                    <p className="text-white font-semibold">{contactInfo.ukAddress || 'London, United Kingdom'}</p>
                    <p className="text-sm text-gray-400 mt-2">🇺🇸 USA Office</p>
                    <p className="text-white font-semibold">{contactInfo.usaAddress || 'New York, USA'}</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-6 flex gap-4">
                {contactInfo.facebook && (
                  <a href={contactInfo.facebook} target="_blank" rel="noopener noreferrer" 
                     className="p-3 bg-darkNavy rounded-full border border-gray-800 hover:border-cyan transition text-gray-400 hover:text-cyan">
                    <FaFacebook size={20} />
                  </a>
                )}
                {contactInfo.linkedin && (
                  <a href={contactInfo.linkedin} target="_blank" rel="noopener noreferrer" 
                     className="p-3 bg-darkNavy rounded-full border border-gray-800 hover:border-cyan transition text-gray-400 hover:text-cyan">
                    <FaLinkedin size={20} />
                  </a>
                )}
                {contactInfo.twitter && (
                  <a href={contactInfo.twitter} target="_blank" rel="noopener noreferrer" 
                     className="p-3 bg-darkNavy rounded-full border border-gray-800 hover:border-cyan transition text-gray-400 hover:text-cyan">
                    <FaTwitter size={20} />
                  </a>
                )}
                {contactInfo.youtube && (
                  <a href={contactInfo.youtube} target="_blank" rel="noopener noreferrer" 
                     className="p-3 bg-darkNavy rounded-full border border-gray-800 hover:border-cyan transition text-gray-400 hover:text-cyan">
                    <FaYoutube size={20} />
                  </a>
                )}
              </div>

              <div className="mt-6 p-6 bg-cyan/5 rounded-xl border border-cyan/20">
                <p className="text-cyan font-semibold">📌 Quick Response</p>
                <p className="text-sm text-gray-400 mt-2">{contactInfo.responseTime || 'We typically respond within 24 hours'}</p>
              </div>
            </div>

            {/* Right Column - Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <input 
                  type="text" 
                  name="name" 
                  placeholder="Your Name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 bg-darkNavy border border-gray-700 rounded-lg text-white focus:border-cyan focus:outline-none" 
                  required 
                />
                <input 
                  type="email" 
                  name="email" 
                  placeholder="Email Address" 
                  value={formData.email} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 bg-darkNavy border border-gray-700 rounded-lg text-white focus:border-cyan focus:outline-none" 
                  required 
                />
                <input 
                  type="tel" 
                  name="phone" 
                  placeholder="Phone Number" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 bg-darkNavy border border-gray-700 rounded-lg text-white focus:border-cyan focus:outline-none" 
                />
                <select 
                  name="service" 
                  value={formData.service} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 bg-darkNavy border border-gray-700 rounded-lg text-white focus:border-cyan focus:outline-none"
                >
                  <option value="">Select a Service</option>
                  <option value="power-bi">Power BI Training</option>
                  <option value="itil">ITIL 4 Certification</option>
                  <option value="whatsapp">WhatsApp Automation</option>
                  <option value="seo">SEO Automation</option>
                  <option value="development">Custom Development</option>
                  <option value="website">Website Development</option>
                  <option value="textile-pos">Textile POS Solution</option>
                  <option value="consulting">IT Consulting</option>
                  <option value="marketing">Digital Marketing</option>
                  <option value="hosting">Hosting & Email</option>
                  <option value="other">Other</option>
                </select>
                <textarea 
                  name="message" 
                  placeholder="Tell us about your project..." 
                  value={formData.message} 
                  onChange={handleChange} 
                  rows="5" 
                  className="w-full px-4 py-3 bg-darkNavy border border-gray-700 rounded-lg text-white focus:border-cyan focus:outline-none" 
                  required 
                />
                <button 
                  type="submit" 
                  className="w-full px-6 py-3 bg-cyan text-navy rounded-full font-semibold hover:bg-cyan/80 transition shadow-lg shadow-cyan/25"
                >
                  Send Message
                </button>
                {submitted && <p className="text-green-400 text-center">{contactInfo.successMessage || '✅ Message sent successfully!'}</p>}
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
